// Import necessary hooks and components
import { useEffect, useState } from 'react'; // React hooks for state and lifecycle management
import { useNavigate } from 'react-router-dom'; // Hook for programmatic navigation
import type { Job } from '../../types/job'; // Type definition for jobs
import { fetchJobs } from '../../api/jobs'; // API function to fetch job data
import { JobList } from '../job/JobList'; // JobList component to display jobs

/**
 * A component to fetch and display the latest 3 job postings.
 */
export function LatestJobs() {
  const [jobs, setJobs] = useState<Job[]>([]); // State for storing fetched jobs
  const [loading, setLoading] = useState(true); // State for loading status
  const navigate = useNavigate(); // Navigation hook

  // Fetch the latest jobs on component mount
  useEffect(() => {
    async function loadLatestJobs() {
      try {
        const data = await fetchJobs(); // Fetch all jobs
        const sortedJobs = data
          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()) // Sort jobs by creation date (newest first)
          .slice(0, 3); // Take the top 3 jobs
        setJobs(sortedJobs); // Update state with sorted jobs
      } catch (error) {
        console.error('Failed to fetch latest jobs:', error); // Log error to the console
      } finally {
        setLoading(false); // Set loading to false once the operation completes
      }
    }

    loadLatestJobs(); // Trigger the function on mount
  }, []); // Empty dependency array ensures this runs only once

  // Handler for job selection
  const handleJobSelect = (jobId: number) => {
    navigate(`/jobs/${jobId}`); // Navigate to the job details page with the job ID
  };

  // Loading state: Show skeleton loaders
  if (loading) {
    return (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {/* Render 3 skeleton loaders for loading state */}
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white p-6 rounded-xl shadow-lg animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div> {/* Title placeholder */}
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div> {/* Subtitle placeholder */}
            <div className="h-4 bg-gray-200 rounded w-1/3"></div> {/* Smaller placeholder */}
          </div>
        ))}
      </div>
    );
  }

  // Loaded state: Render the JobList component
  return (
    <JobList
      jobs={jobs} // Pass the fetched jobs to the JobList component
      onSelectJob={handleJobSelect} // Pass the job selection handler
    />
  );
}
